import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiV2 } from 'src/api/apiv2';
import { HEADERS } from 'src/constants/headers';
import { useWarboxMessage } from 'src/hooks';
import type { IMessage } from 'src/interfaces/message';
import {
    buildMediaGalleryMessageFilters,
    type TMediaGalleryTab,
} from '../constants/mediaGallery';
import {
    buildConversationMediaGallery,
    type TGroupedConversationMedia,
    warboxMessageHasMedia,
} from '../utils/conversationMediaGallery';

type TUseConversationMediaGalleryOptions = {
    conversationId?: string;
    tab?: TMediaGalleryTab;
    enabled?: boolean;
    limit?: number;
    mode?: 'shared' | 'local';
};

export const useConversationMediaGallery = ({
    conversationId,
    tab = 'all',
    enabled = true,
    limit,
    mode = 'shared',
}: TUseConversationMediaGalleryOptions) => {
    const {
        galleryMessages,
        galleryTotal,
        galleryLoading,
        getMessagesByConversation,
        messages: threadMessages,
    } = useWarboxMessage();

    const [localMessages, setLocalMessages] = useState<IMessage[] | null>(null);
    const [localTotal, setLocalTotal] = useState<number | null>(null);
    const [localLoading, setLocalLoading] = useState(false);
    const lastThreadMediaSignatureRef = useRef('');
    const getMessagesByConversationRef = useRef(getMessagesByConversation);

    getMessagesByConversationRef.current = getMessagesByConversation;

    const fetchGallery = useCallback(async () => {
        if (!conversationId || !enabled) return;

        const filters = buildMediaGalleryMessageFilters(tab, {
            ...(limit ? { limit } : {}),
        });

        if (mode === 'local') {
            setLocalLoading(true);

            try {
                const res = await ApiV2.post(
                    `/conversations/${conversationId}/messages`,
                    filters,
                    HEADERS()
                );

                const messages: IMessage[] = res.data?.data ?? [];
                const total =
                    res.data?.pagination?.total ??
                    res.data?.count ??
                    messages.length;

                setLocalMessages(messages);
                setLocalTotal(total);
            } catch {
                setLocalMessages([]);
                setLocalTotal(0);
            } finally {
                setLocalLoading(false);
            }

            return;
        }

        await getMessagesByConversationRef.current(conversationId, filters, {
            storeTarget: 'gallery',
        });
    }, [conversationId, enabled, limit, mode, tab]);

    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    const threadMediaSignature = useMemo(
        () =>
            (threadMessages ?? [])
                .filter(warboxMessageHasMedia)
                .map((message) => message._id)
                .join('|'),
        [threadMessages]
    );

    useEffect(() => {
        if (!conversationId || !enabled) return;

        if (lastThreadMediaSignatureRef.current === threadMediaSignature) {
            return;
        }

        const hadPreviousSignature = Boolean(
            lastThreadMediaSignatureRef.current
        );
        lastThreadMediaSignatureRef.current = threadMediaSignature;

        if (hadPreviousSignature) {
            fetchGallery();
        }
    }, [conversationId, enabled, fetchGallery, threadMediaSignature]);

    const sourceMessages =
        mode === 'local' ? localMessages ?? [] : galleryMessages ?? [];
    const resolvedTotal =
        mode === 'local' ? localTotal : galleryTotal;
    const resolvedLoading =
        mode === 'local' ? localLoading : galleryLoading;

    const groupedMedia = useMemo<TGroupedConversationMedia>(
        () => buildConversationMediaGallery(sourceMessages),
        [sourceMessages]
    );

    return {
        groupedMedia,
        galleryTotal: resolvedTotal,
        galleryLoading: resolvedLoading,
        refetchGallery: fetchGallery,
    };
};
